import { SxProps, Theme } from '@mui/material'

/**
 * Responsive breakpoints for the application
 * These values MUST match the MUI theme breakpoints in theme/index.ts
 *
 * Mobile-first approach: xs -> sm -> md -> lg -> xl
 *
 * MUI interprets these breakpoints as min-width queries:
 * - xs applies at 0px and up
 * - sm applies at 375px and up
 * - md applies at 768px and up
 * - lg applies at 1023px and up
 * - xl applies at 1536px and up
 *
 * VIEWPORT DETECTION (useViewport hook):
 * The useViewport hook uses different thresholds for feature availability:
 * - mobile: < 600px (phones only)
 * - tablet: 600px - 768px inclusive (small tablets)
 * - desktop: > 768px (includes laptops, split screens)
 *
 * This ensures split-screen laptops (~950px) are treated as desktop,
 * not mobile, for feature availability checks.
 */
export const breakpoints = {
  xs: 0, // Extra small devices, all screen sizes
  sm: 375, // Small devices, iPhone X/11/12, standard phones
  md: 768, // Medium devices, tablets in portrait mode
  lg: 1023, // Large devices, tablets in landscape, small laptops
  xl: 1536, // Extra large devices, desktops
} as const

/**
 * Media query helpers
 * These generate the same breakpoint behavior as MUI's theme
 *
 * IMPORTANT: These values must stay in sync with the MUI theme in theme/index.ts
 * and with the breakpoints object above.
 */
export const mediaQueries = {
  xs: `@media (min-width: ${breakpoints.xs}px)`,
  sm: `@media (min-width: ${breakpoints.sm}px)`,
  md: `@media (min-width: ${breakpoints.md}px)`,
  lg: `@media (min-width: ${breakpoints.lg}px)`,
  xl: `@media (min-width: ${breakpoints.xl}px)`,
} as const

/**
 * Spacing scale for consistent spacing across breakpoints
 *
 * Note: 'tablet' is a custom alias, not a standard MUI breakpoint.
 * When used with the responsive() helper, it maps to 'lg'.
 */
export const spacing = {
  xs: {
    page: '16px',
    section: '24px',
    component: '16px',
    element: '8px',
  },
  sm: {
    page: '20px',
    section: '32px',
    component: '20px',
    element: '12px',
  },
  md: {
    page: '24px',
    section: '40px',
    component: '24px',
    element: '16px',
  },
  tablet: {
    page: '32px',
    section: '48px',
    component: '32px',
    element: '20px',
  },
  lg: {
    page: '40px',
    section: '64px',
    component: '40px',
    element: '24px',
  },
} as const

/**
 * Typography scale for responsive font sizes
 *
 * Note: 'tablet' is a custom alias, not a standard MUI breakpoint.
 * When used with the responsive() helper, it maps to 'lg'.
 */
export const typography = {
  h1: {
    xs: '24px',
    sm: '28px',
    md: '32px',
    tablet: '40px',
    lg: '48px',
  },
  h2: {
    xs: '20px',
    sm: '24px',
    md: '28px',
    tablet: '32px',
    lg: '40px',
  },
  h3: {
    xs: '18px',
    sm: '20px',
    md: '24px',
    tablet: '28px',
    lg: '32px',
  },
  h4: {
    xs: '16px',
    sm: '18px',
    md: '20px',
    tablet: '24px',
    lg: '28px',
  },
  body1: {
    xs: '14px',
    sm: '14px',
    md: '16px',
    tablet: '16px',
    lg: '16px',
  },
  body2: {
    xs: '12px',
    sm: '13px',
    md: '14px',
    tablet: '14px',
    lg: '14px',
  },
} as const

/**
 * Container max widths for different breakpoints
 */
export const containerMaxWidths = {
  xs: '100%',
  sm: '100%',
  md: '400px',
  tablet: '490px',
  lg: '560px',
} as const

/**
 * Helper function to create responsive styles with SX prop
 */
export const createResponsiveSx = <T extends Record<string, unknown>>(styles: T): SxProps<Theme> => {
  return styles as SxProps<Theme>
}

/**
 * Helper to create responsive value objects for MUI sx prop
 *
 * Maps custom breakpoints to MUI breakpoints:
 * - `tablet` is an alias for `lg` (for backward compatibility)
 * - If both `lg` and `tablet` are provided, `lg` takes priority
 * - All standard MUI breakpoints (xs, sm, md, lg, xl) are supported directly
 *
 * @example
 * responsive({ xs: '14px', tablet: '20px' })
 * // Returns: { xs: '14px', lg: '20px' }
 *
 * responsive({ xs: '14px', lg: '20px', xl: '24px' })
 * // Returns: { xs: '14px', lg: '20px', xl: '24px' }
 */
export const responsive = <T>(values: { xs?: T; sm?: T; md?: T; tablet?: T; lg?: T; xl?: T }) => {
  return {
    xs: values.xs,
    sm: values.sm,
    md: values.md,
    lg: values.lg ?? values.tablet, // Use lg if provided, fallback to tablet
    xl: values.xl,
  }
}

/**
 * Padding presets for common use cases
 */
export const paddingPresets = {
  page: {
    xs: 2,
    sm: 2.5,
    md: 3,
    lg: 4,
    xl: 5,
  },
  section: {
    xs: 3,
    sm: 4,
    md: 5,
    lg: 6,
    xl: 8,
  },
  card: {
    xs: 2,
    sm: 2.5,
    md: 3,
    lg: 3,
    xl: 3,
  },
} as const

/**
 * Margin presets for common use cases
 */
export const marginPresets = {
  small: {
    xs: 1,
    sm: 1.5,
    md: 2,
    lg: 2,
    xl: 2,
  },
  medium: {
    xs: 2,
    sm: 3,
    md: 4,
    lg: 4,
    xl: 4,
  },
  large: {
    xs: 3,
    sm: 4,
    md: 6,
    lg: 8,
    xl: 12,
  },
} as const
